import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Refs to handle browser auto-fill
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const from = location.state?.from?.pathname || '/dashboard';
  
  // Handle browser auto-fill - sync DOM values to state on mount
  useEffect(() => {
    const syncAutoFill = () => {
      if (emailRef.current && emailRef.current.value && !email) {
        setEmail(emailRef.current.value);
      }
      if (passwordRef.current && passwordRef.current.value && !password) {
        setPassword(passwordRef.current.value);
      }
    };
    
    // Check immediately and after a short delay (for slow auto-fill)
    syncAutoFill();
    const timer = setTimeout(syncAutoFill, 100);
    const timer2 = setTimeout(syncAutoFill, 500);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Get values directly from DOM to handle auto-fill edge cases
    const emailValue = emailRef.current?.value || email;
    const passwordValue = passwordRef.current?.value || password;
    
    if (!emailValue || !passwordValue) {
      toast.error('Please enter email and password');
      setLoading(false);
      return;
    }

    try {
      const result = await login(emailValue, passwordValue);
      toast.success('Welcome back!');
      
      // Route based on role
      let targetPath = '/dashboard';
      if (result.user.role === 'supporter') {
        targetPath = '/supporter';
      } else if (result.user.role === 'org_admin') {
        targetPath = '/organization';
      }
      
      navigate(targetPath, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-aura-bg flex flex-col">
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-aura-text-muted hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back</span>
        </Link>
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="/aura-icon.png" 
            alt="Aura" 
            className="w-9 h-9 object-contain"
          />
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white uppercase tracking-tight mb-2">
              Aura Athlete Development
            </h1>
            <p className="text-aura-text-muted">
              Aura is earned, not given.
            </p>
          </div>

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 border-white/20 text-white hover:bg-white/5 mb-6"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-aura-bg text-aura-text-subtle">or sign in with email</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-aura-text-subtle">
                Email
              </Label>
              <Input
                id="email"
                ref={emailRef}
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="athlete@example.com"
                required
                className="h-12 bg-aura-surface border-white/10 text-white placeholder:text-aura-text-subtle focus:border-aura-primary focus:ring-aura-primary"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-aura-text-subtle">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  ref={passwordRef}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="h-12 bg-aura-surface border-white/10 text-white placeholder:text-aura-text-subtle focus:border-aura-primary focus:ring-aura-primary pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-aura-text-subtle hover:text-white transition-colors p-1"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="text-center mt-4">
            <Link 
              to="/forgot-password" 
              className="text-sm text-aura-text-muted hover:text-aura-primary transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </Link>
          </div>

          <p className="text-center text-aura-text-muted mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-aura-primary hover:text-aura-primary-hover font-medium">
              Start free trial
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
